// ─── referralBaul.jsx ─────────────────────────────────────────────────────────
// Archivo central único para el baúl de referidos.
//
// Persiste TODO bajo una sola clave JSON ("clinifica_referral_v2") para
// garantizar atomicidad: nunca puede quedar la foto de un profesional con
// el mensaje de otro.
//
// API pública:
//   escribirBaul(apiResponse, slug)  → guarda el baúl completo
//   leerBaul()                       → devuelve el objeto del baúl o null
//   limpiarBaul()                    → borra la clave completa
//   limpiarBaulSiExpirado()          → borra sólo si superó 28 días
// ──────────────────────────────────────────────────────────────────────────────

const BAUL_KEY = "clinifica_referral_v2";
const DIAS_EXPIRACION = 28;
const MS_EXPIRACION = DIAS_EXPIRACION * 24 * 60 * 60 * 1000;

// Slug de la alianza DuoEmprende (Medical Funnel), con landing personalizada
const SLUG_DUOEMPRENDE = "duoemprende";

const DEFAULT_PHOTO_URL =
  "https://teradomus-storage-public.s3.us-east-1.amazonaws.com/perfil/default-final-250901011556.webp";

const PASTEL_COLORS = [
  "#FFB3BA", "#BAFFC9", "#BAE1FF", "#E8BAFF",
  "#B3FFEC", "#C9B3FF", "#FFB3E6",
];

const getPastelColor = (letter) => {
  if (!letter) return PASTEL_COLORS[0];
  return PASTEL_COLORS[letter.toUpperCase().charCodeAt(0) % PASTEL_COLORS.length];
};

// ─── Helpers de texto ────────────────────────────────────────────────────────

const toTitleCase = (str) =>
  str.replace(/__/g, " ").split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const getPrimerNombre = (perfil) => {
  if (perfil?.profesional_nombres)
    return toTitleCase(perfil.profesional_nombres.split(" ")[0]);
  if (perfil?.name)
    return toTitleCase(perfil.name.split(" ")[0]);
  return "Un profesional";
};

const buildGiftLabels = (giftVouchers) => {
  if (!giftVouchers || giftVouchers.length === 0) return [];

  const grouped = giftVouchers.reduce((acc, offer) => {
    const key = `${offer.billing_frequency}_${offer.offer_type}_${offer.offer_fixed_price || 0}_${offer.offer_discount_amount || 0}_${offer.offer_discount_percentage || 0}`;
    if (!acc[key]) acc[key] = { count: 0, offer };
    acc[key].count += 1;
    return acc;
  }, {});

  return Object.values(grouped).map(({ count, offer }) => {
    const periodText =
      offer.billing_frequency === "ANNUAL"
        ? count === 1 ? "año" : "años"
        : count === 1 ? "mes" : "meses";

    if (offer.offer_type === "FIXED_PRICE")
      return `${count} ${periodText} a $${Number(offer.offer_fixed_price || 0).toLocaleString("es-CL")}`;
    if (offer.offer_type === "DISCOUNT_PERCENTAGE")
      return `${count} ${periodText} con ${offer.offer_discount_percentage}% de dcto`;
    if (offer.offer_type === "DISCOUNT_FIXED")
      return `$${Number(offer.offer_discount_amount || 0).toLocaleString("es-CL")} de dcto por ${count} ${periodText}`;
    return "";
  }).filter(Boolean);
};

const buildMensajeInvitacion = (apiResponse) => {
  const perfil = apiResponse?.profile;
  const giftVouchers = apiResponse?.referral_program?.gift_vouchers || [];
  const primerNombre = getPrimerNombre(perfil);
  const labels = buildGiftLabels(giftVouchers);

  if (labels.length === 0)
    return `${primerNombre} te regala 1 mes a $990 para que pruebes el plan Pro de Clinifica`;
  if (labels.length === 1)
    return `${primerNombre} te regala ${labels[0]} para que pruebes el plan Pro de Clinifica`;
  const last = labels[labels.length - 1];
  return `${primerNombre} te regala ${labels.slice(0, -1).join(", ")} y ${last} para que pruebes el plan Pro de Clinifica`;
};

// Beneficio en formato destacado para la alianza DuoEmprende (ej: "25% por 6 meses"),
// siempre calculado desde los cupones del programa para que coincida con la BD
const buildBeneficioDestacado = (giftVouchers) => {
  if (!giftVouchers || giftVouchers.length === 0) return "";

  const grouped = giftVouchers.reduce((acc, offer) => {
    const key = `${offer.billing_frequency}_${offer.offer_type}_${offer.offer_fixed_price || 0}_${offer.offer_discount_amount || 0}_${offer.offer_discount_percentage || 0}`;
    if (!acc[key]) acc[key] = { count: 0, offer };
    acc[key].count += 1;
    return acc;
  }, {});

  const labels = Object.values(grouped).map(({ count, offer }) => {
    const periodText =
      offer.billing_frequency === "ANNUAL"
        ? count === 1 ? "año" : "años"
        : count === 1 ? "mes" : "meses";

    if (offer.offer_type === "DISCOUNT_PERCENTAGE")
      return `${offer.offer_discount_percentage}% por ${count} ${periodText}`;
    if (offer.offer_type === "FIXED_PRICE")
      return `${count} ${periodText} a $${Number(offer.offer_fixed_price || 0).toLocaleString("es-CL")}`;
    if (offer.offer_type === "DISCOUNT_FIXED")
      return `$${Number(offer.offer_discount_amount || 0).toLocaleString("es-CL")} por ${count} ${periodText}`;
    return "";
  }).filter(Boolean);

  if (labels.length <= 1) return labels[0] || "";
  const last = labels[labels.length - 1];
  return `${labels.slice(0, -1).join(", ")} y ${last}`;
};

const buildMensajeCorto = (apiResponse) => {
  const giftVouchers = apiResponse?.referral_program?.gift_vouchers || [];
  const labels = buildGiftLabels(giftVouchers);

  if (labels.length === 0) return "1 mes a $990";
  if (labels.length === 1) return labels[0];
  const last = labels[labels.length - 1];
  return `${labels.slice(0, -1).join(", ")} y ${last}`;
};

// ─── API pública ─────────────────────────────────────────────────────────────

/**
 * Guarda el baúl completo como un único JSON atómico.
 * Siempre debe llamarse DESPUÉS de limpiarBaul() para evitar contaminación.
 *
 * @param {object} apiResponse  Respuesta completa de /agenda/referidosPerfil
 * @param {string} slug         Slug del link referido (ej: "adipa", "juanperez")
 */
export function escribirBaul(apiResponse, slug) {
  const perfil = apiResponse?.profile;
  const rawFoto = perfil?.profesional_foto_perfil;
  const foto = rawFoto === DEFAULT_PHOTO_URL || !rawFoto ? null : rawFoto;

  // Color pastel: siempre se regenera desde la primera letra del nombre
  // para que nunca quede un color de un referido anterior
  const primerNombre = getPrimerNombre(perfil);
  const colorPastel = foto ? null : getPastelColor(primerNombre ? primerNombre[0] : "?");

  // Alianza DuoEmprende (Medical Funnel): landing y banner con textos propios
  const esDuoEmprende =
    typeof slug === "string" && slug.toLowerCase() === SLUG_DUOEMPRENDE;
  const beneficioDuo = esDuoEmprende
    ? buildBeneficioDestacado(apiResponse?.referral_program?.gift_vouchers || [])
    : null;

  const mensajeInvitacion = esDuoEmprende
    ? `Por ser parte de Medical Funnel, accede a un descuento exclusivo del ${beneficioDuo} en el Plan Pro de Clinifica`
    : buildMensajeInvitacion(apiResponse);
  const mensajeCorto = buildMensajeCorto(apiResponse);
  const mensajeExito = esDuoEmprende
    ? `Por ser parte de Medical Funnel, tienes un descuento exclusivo del ${beneficioDuo} en el Plan Pro de Clinifica`
    : mensajeInvitacion.replace("te regala", "te ha regalado");

  const esAdipa =
    typeof slug === "string" && slug.toLowerCase().startsWith("adipa");

  const baul = {
    slug,
    timestamp: Date.now(),
    foto,
    colorPastel,
    idProfFantasma: perfil?.id_profesional_fantasma || null,
    linkPlataforma: perfil?.profesional_link_plataforma || null,
    mensajeInvitacion,
    mensajeCorto,
    mensajeExito,
    esAdipa,
    esDuoEmprende,
    beneficioDuo,
  };

  localStorage.setItem(BAUL_KEY, JSON.stringify(baul));
  return baul;
}

/**
 * Lee el baúl y devuelve el objeto guardado, o null si está vacío / corrupto.
 */
export function leerBaul() {
  try {
    const raw = localStorage.getItem(BAUL_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Borra completamente el baúl de referidos del localStorage.
 * Llamar al completar exitosamente el registro, o al entrar a /ref/slug
 * antes de escribir el nuevo referido.
 */
export function limpiarBaul() {
  localStorage.removeItem(BAUL_KEY);
}

/**
 * Verifica si el baúl lleva más de 28 días guardado.
 * Si expiró, lo borra antes de que cualquier componente lo lea.
 * Llamar al montar Planes.jsx y Registro.jsx.
 */
export function limpiarBaulSiExpirado() {
  const baul = leerBaul();
  if (!baul?.timestamp) return;
  if (Date.now() - baul.timestamp > MS_EXPIRACION) {
    limpiarBaul();
  }
}
